import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot } from 'lexical';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { forwardRef, useCallback, useRef, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import { composeRefs } from 'lib/utils';

import ParagraphNode from './Nodes/ParagraphNode';
import RichText from './RichText';
import SendButton from './SendButton';

const Toolbar = dynamic(() => import('./Toolbar'), { ssr: false });
const ErrorMessage = dynamic(() => import('./ErrorMessage'), { ssr: false });
const Label = dynamic(() => import('./Label'));

const TextEditor = forwardRef(
  (
    {
      answering,
      className,
      disabled = false,
      error = null,
      gaEventCategory,
      initialValue,
      label,
      placeholder,
      toolbarItems,
      onBlur,
      onCancel,
      onChange,
      onFocus,
      onSend,
    },
    ref
  ) => {
    const editorRef = useRef(null);
    const [editor] = useLexicalComposerContext();
    const [isSendEnabled, setSendEnabled] = useState(false);
    const [isToolbarVisible, setToolbarVisible] = useState(false);

    const onChangeHandler = useCallback(
      (editorState) => {
        editor.update(() => {
          const isEditorEmpty = $getRoot()
            .getChildren()
            .every(
              (child) => child instanceof ParagraphNode && child.isEmpty()
            );

          setSendEnabled(!isEditorEmpty);
        });

        if (onChange) {
          onChange(editorState);
        }
      },
      [editor, onChange]
    );

    const handleFocus = useCallback(() => {
      setToolbarVisible(true);

      if (onFocus) {
        onFocus();
      }
    }, [onFocus]);

    const handleBlur = useCallback(() => {
      setToolbarVisible(false);

      if (onBlur) {
        onBlur();
      }
    }, [onBlur]);

    return (
      <>
        {label && <Label>{label}</Label>}
        <div
          ref={composeRefs(ref, editorRef)}
          className={twJoin('w-full', className)}
        >
          {/*
            RichText + Toolbar vivem na mesma coluna flex-1; o SendButton fica
            ao lado com `self-center` pra permanecer alinhado verticalmente
            mesmo quando a Toolbar abre e estica a coluna. Sem isso o SendButton
            ficava "flutuando" no topo enquanto o campo crescia.
          */}
          <div className="flex items-stretch gap-x-2">
            <div className="group flex min-w-0 flex-1 flex-col">
              <RichText
                answering={answering}
                className={className}
                disabled={disabled}
                error={!!error}
                hasToolbar={isToolbarVisible}
                initialValue={initialValue}
                placeholder={placeholder}
                onBlur={handleBlur}
                onChange={onChangeHandler}
                onFocus={handleFocus}
                onSend={onSend}
              />
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
              <div
                aria-hidden={!isToolbarVisible}
                className={twJoin(
                  'overflow-hidden transition-[max-height,opacity] duration-200 ease-in-out motion-reduce:transition-none',
                  isToolbarVisible
                    ? 'max-h-40 opacity-100'
                    : 'pointer-events-none max-h-0 opacity-0'
                )}
                onMouseDown={(e) => e.preventDefault()}
              >
                <Toolbar
                  gaEventCategory={gaEventCategory}
                  isSendEnabled={isSendEnabled}
                  selectedTools={toolbarItems}
                  withCancel={!!onCancel}
                  withSend={false}
                  onCancel={onCancel}
                  onSend={onSend}
                />
              </div>
            </div>
            {onSend && (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions
              <div
                className="flex shrink-0 items-center self-center"
                onMouseDown={(e) => e.preventDefault()}
              >
                <SendButton
                  disabled={!isSendEnabled}
                  gaEventCategory={gaEventCategory}
                  onClick={onSend}
                />
              </div>
            )}
          </div>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </div>
      </>
    );
  }
);

TextEditor.displayName = 'TextEditor';

TextEditor.propTypes = {
  /**
   * Who user is answering in text-editor
   */
  answering: PropTypes.string,
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /**
   * Whether the text-editor field is disabled
   */
  disabled: PropTypes.bool,
  /**
   * Whether the text-editor should render a error state
   */
  error: PropTypes.string,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Initial value for uncontrolled component
   */
  initialValue: PropTypes.string,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * GA Events
   */
  gaEventCategory: PropTypes.string,
  /**
   * Text to be rendered as a label
   */
  label: PropTypes.string,
  /**
   * Text that appears in the form control when the it is empty
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * Function to set the visibility of comment tooltip
   */
  toolbarItems: PropTypes.arrayOf(PropTypes.string),
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on blur
   */
  onBlur: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on cancel
   */
  onCancel: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on change
   */
  onChange: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on focus
   */
  onFocus: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback function on send
   */
  onSend: PropTypes.func,
};

export default TextEditor;
